// Offline cache (Phase 1, Step 1): IndexedDB-backed stale-while-revalidate
// for GET requests, plus typed stores we'll grow into across steps 2-6.
//
// Why IndexedDB and not localStorage?
//  - We need to cache potentially large event lists + travel estimates.
//  - localStorage is sync + size-capped (~5MB) and blocks the main thread.
//  - IndexedDB gives us per-user isolation, structured queries, and room.
//
// We use the `idb` wrapper for ergonomics; if it's not yet installed the
// module is lazy-loaded so the app boots either way (the cache simply
// becomes a no-op until the dep lands). That keeps Step 1 mergeable.

const DB_NAME = "fretless_offline_v1";
const DB_VERSION = 1;

// Object stores. Keep names stable — bumping VERSION is the only safe rename.
export const STORE = {
  RESPONSES: "responses",     // raw GET response cache, keyed by URL
  EVENTS: "events",            // normalised events, keyed by id
  TRAVEL: "travel_estimates",  // last-known travel estimate per event id
  PROFILE: "user_profile",     // single-user profile blob, keyed "me"
  MUTATIONS: "mutation_queue", // queued writes for Step 4
  META: "meta",                // misc (last_synced timestamps, etc.)
};

let _idbPromise = null;
let _idbModulePromise = null;

const getIdbModule = async () => {
  if (_idbModulePromise) return _idbModulePromise;
  _idbModulePromise = import("idb").catch((err) => {
    // `idb` not installed yet — degrade to no-op so the app keeps working.
    // eslint-disable-next-line no-console
    console.warn("[offlineCache] idb not available, cache disabled:", err?.message);
    return null;
  });
  return _idbModulePromise;
};

const getDB = async () => {
  if (_idbPromise) return _idbPromise;
  const mod = await getIdbModule();
  if (!mod) return null;
  _idbPromise = mod.openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE.RESPONSES)) {
        db.createObjectStore(STORE.RESPONSES); // keyPath null — we supply URL key
      }
      if (!db.objectStoreNames.contains(STORE.EVENTS)) {
        db.createObjectStore(STORE.EVENTS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE.TRAVEL)) {
        db.createObjectStore(STORE.TRAVEL); // keyed by event id (caller-provided)
      }
      if (!db.objectStoreNames.contains(STORE.PROFILE)) {
        db.createObjectStore(STORE.PROFILE);
      }
      if (!db.objectStoreNames.contains(STORE.MUTATIONS)) {
        // autoIncrement so we get FIFO ordering for free.
        db.createObjectStore(STORE.MUTATIONS, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE.META)) {
        db.createObjectStore(STORE.META);
      }
    },
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[offlineCache] openDB failed:", err?.message);
    return null;
  });
  return _idbPromise;
};

// --- low-level put/get helpers ---------------------------------------------

const put = async (store, value, key) => {
  const db = await getDB();
  if (!db) return false;
  try {
    await db.put(store, value, key);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[offlineCache] put(${store}) failed:`, err?.message);
    return false;
  }
};

const get = async (store, key) => {
  const db = await getDB();
  if (!db) return null;
  try {
    return await db.get(store, key);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[offlineCache] get(${store}) failed:`, err?.message);
    return null;
  }
};

const delKey = async (store, key) => {
  const db = await getDB();
  if (!db) return false;
  try {
    await db.delete(store, key);
    return true;
  } catch {
    return false;
  }
};

// --- response cache (URL keyed) --------------------------------------------

// Normalise the cache key. We deliberately key on path + sorted query so two
// equivalent URLs collapse to one entry. We strip the leading `/api` prefix
// because the same logical endpoint may be served from `/api/...` (relative
// API const) or absolute (REACT_APP_API_URL) and we don't want to double-cache.
export const cacheKeyForUrl = (url) => {
  if (!url) return null;
  let path = url;
  try {
    // Handle absolute URLs by parsing them; relative paths fall through to raw.
    if (/^https?:\/\//i.test(url)) {
      const u = new URL(url);
      const params = new URLSearchParams(u.search);
      params.sort();
      const qs = params.toString();
      path = u.pathname + (qs ? "?" + qs : "");
    } else {
      const [p, q = ""] = url.split("?");
      if (q) {
        const params = new URLSearchParams(q);
        params.sort();
        path = p + "?" + params.toString();
      } else {
        path = p;
      }
    }
  } catch {
    path = url;
  }
  return path.replace(/^\/api(?=\/)/, "");
};

export const putResponse = async (url, data) => {
  const key = cacheKeyForUrl(url);
  if (!key) return false;
  return put(STORE.RESPONSES, { data, cachedAt: Date.now() }, key);
};

export const getResponse = async (url) => {
  const key = cacheKeyForUrl(url);
  if (!key) return null;
  return get(STORE.RESPONSES, key);
};

// --- entity stores ---------------------------------------------------------

export const putEvents = async (events) => {
  if (!Array.isArray(events) || events.length === 0) return false;
  const db = await getDB();
  if (!db) return false;
  try {
    const tx = db.transaction(STORE.EVENTS, "readwrite");
    await Promise.all(events.map((e) => e && e.id && tx.store.put(e)));
    await tx.done;
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[offlineCache] putEvents failed:", err?.message);
    return false;
  }
};

export const getAllEvents = async () => {
  const db = await getDB();
  if (!db) return [];
  try {
    return await db.getAll(STORE.EVENTS);
  } catch {
    return [];
  }
};

export const putTravelEstimate = async (eventId, estimate) => {
  if (!eventId || !estimate) return false;
  return put(STORE.TRAVEL, { ...estimate, cachedAt: Date.now() }, eventId);
};

export const getTravelEstimate = (eventId) => get(STORE.TRAVEL, eventId);

export const putProfile = (profile) => put(STORE.PROFILE, profile, "me");
export const getProfile = () => get(STORE.PROFILE, "me");
export const clearProfile = () => delKey(STORE.PROFILE, "me");

// --- meta (last-synced, schema flags, etc.) --------------------------------

export const setMeta = (key, value) => put(STORE.META, value, key);
export const getMeta = (key) => get(STORE.META, key);

// --- introspection for tests / debug ---------------------------------------

export const _internal = { getDB, DB_NAME, DB_VERSION };
